import { useState } from 'react';
import {
  useDeletePronunciationMutation,
  useGetPronunciationsQuery,
  usePreviewVoiceMutation,
  useSavePronunciationMutation,
  useSetSpokenNameMutation,
  type SpokenNameEntry
} from '@/store/configApi';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, EmptyState, Panel, SkeletonRows } from '@/components/ui';

/**
 * Pronunciation overrides.
 *
 * A line that mispronounces a restaurant's own dishes sounds worse than one
 * with a plainer voice, so this is the screen that gets a place live rather
 * than merely working.
 */
export function PronunciationEditor({ locationId }: { locationId: string }) {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useGetPronunciationsQuery(locationId, { skip: !locationId });

  const [saveTerm, savingTerm] = useSavePronunciationMutation();
  const [deleteTerm] = useDeletePronunciationMutation();
  const [setSpokenName] = useSetSpokenNameMutation();
  const [preview] = usePreviewVoiceMutation();

  const [draft, setDraft] = useState({ display: '', spoken: '' });

  const listen = async (text: string) => {
    const result = await preview({ locationId, text });
    if ('error' in result || !result.data.audioUrl) {
      dispatch(pushToast('No speech provider configured, so there is nothing to play.', 'info'));
      return;
    }
    await new Audio(result.data.audioUrl).play().catch(() => undefined);
  };

  const addTerm = async () => {
    if (!draft.display.trim() || !draft.spoken.trim()) {
      dispatch(pushToast('Both the written and spoken forms are needed.', 'error'));
      return;
    }

    const result = await saveTerm({ locationId, ...draft });
    if ('error' in result) {
      dispatch(pushToast('Could not save that pronunciation.', 'error'));
      return;
    }
    setDraft({ display: '', spoken: '' });
    dispatch(pushToast('Saved. Stored audio will be regenerated.', 'success'));
  };

  const saveSpoken = async (entry: SpokenNameEntry, spokenName: string) => {
    const result = await setSpokenName({ kind: entry.kind, id: entry.id, spokenName });
    dispatch(
      'error' in result
        ? pushToast('Could not save that pronunciation.', 'error')
        : pushToast('Saved.', 'success')
    );
  };

  return (
    <Panel title="How words are said">
      <div className="px-4 py-4">
        <p className="max-w-2xl text-small text-slate">
          Write the pronunciation the way you would say it out loud, in syllables — for
          example <span className="text-ink">cho-lay bha-too-ray</span>. This changes only how
          a word sounds, never what the line tells a caller.
        </p>

        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : (
          <>
            <h3 className="mt-5 text-small font-medium text-ink">Menu items</h3>
            {data?.menuItems.length === 0 ? (
              <EmptyState
                headline="No menu items yet"
                hint="Sync your menu first, then set pronunciations for anything the voice gets wrong."
              />
            ) : (
              <ul className="mt-2 divide-y divide-rule border-y border-rule">
                {data?.menuItems.map((entry) => (
                  <SpokenRow
                    key={entry.id}
                    entry={entry}
                    onSave={saveSpoken}
                    onListen={listen}
                  />
                ))}
              </ul>
            )}

            {(data?.modifiers.length ?? 0) > 0 ? (
              <>
                <h3 className="mt-6 text-small font-medium text-ink">Options</h3>
                <ul className="mt-2 divide-y divide-rule border-y border-rule">
                  {data?.modifiers.map((entry) => (
                    <SpokenRow
                      key={entry.id}
                      entry={entry}
                      onSave={saveSpoken}
                      onListen={listen}
                    />
                  ))}
                </ul>
              </>
            ) : null}

            <h3 className="mt-6 text-small font-medium text-ink">Other words</h3>
            <p className="text-micro text-slate">
              Your restaurant name, a street name, anything else the line says aloud.
            </p>

            {(data?.terms.length ?? 0) > 0 ? (
              <ul className="mt-2 divide-y divide-rule border-y border-rule">
                {data?.terms.map((term) => (
                  <li key={term._id} className="flex flex-wrap items-center gap-3 py-2.5">
                    <span className="text-small text-ink">{term.display}</span>
                    <span className="text-small text-slate">said as “{term.spoken}”</span>
                    <div className="ml-auto flex gap-3">
                      <button
                        onClick={() => listen(term.spoken)}
                        className="text-small text-jade underline underline-offset-2"
                      >
                        Listen
                      </button>
                      <button
                        onClick={() => deleteTerm(term._id)}
                        className="text-small text-slate hover:text-rust"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <input
                value={draft.display}
                onChange={(event) => setDraft({ ...draft, display: event.target.value })}
                placeholder="Written, e.g. Chole Bhature"
                className="rounded border border-rule bg-white px-3 py-2 text-small"
                aria-label="Written form"
              />
              <input
                value={draft.spoken}
                onChange={(event) => setDraft({ ...draft, spoken: event.target.value })}
                placeholder="Spoken, e.g. cho-lay bha-too-ray"
                className="rounded border border-rule bg-white px-3 py-2 text-small"
                aria-label="Spoken form"
              />
              <Button loading={savingTerm.isLoading} onClick={addTerm}>
                Add
              </Button>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

function SpokenRow({
  entry,
  onSave,
  onListen
}: {
  entry: SpokenNameEntry;
  onSave: (entry: SpokenNameEntry, spokenName: string) => void;
  onListen: (text: string) => void;
}) {
  const [value, setValue] = useState(entry.spoken);
  const dirty = value.trim() !== entry.spoken.trim();

  return (
    <li className="flex flex-wrap items-center gap-3 py-2.5">
      <span className="w-48 shrink-0 text-small text-ink">{entry.display}</span>
      <input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Say it as…"
        className="min-w-[12rem] flex-1 rounded border border-rule bg-white px-3 py-1.5 text-small"
        aria-label={`Pronunciation for ${entry.display}`}
      />
      <button
        onClick={() => onListen(value || entry.display)}
        className="text-small text-jade underline underline-offset-2"
      >
        Listen
      </button>
      {dirty ? (
        <Button onClick={() => onSave(entry, value)}>Save</Button>
      ) : null}
    </li>
  );
}
